import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  FileListData,
  MultipleStepsAnswer,
  StepChangeDirection,
  ThreeStepsDetails,
} from '@typings/ResidentTypes';
import { ErrorsProvider, useErrors } from '@contexts/ErrorsContext';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import {
  updateErrors,
  formatDate,
  parseFormattedDate,
  validatePastDateMessage,
  successMessageInMiddleOfSteps,
} from '@utils/commons';
import NewErrorSummary from '@components/NewErrorSummary';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import ACPConsent from './acp-consent/ACPConsent';
import ReviewDate, { ReviewDateType } from './components/ReviewDate';
import {
  questions,
  ShowSuccessNotificationType,
  SuccessNotiPositionEnum,
} from '../commons';
import MentalCapacitySafeGuarding from './components/mental-capactity-safeguarding';
import { ACPConsentData } from './acp-consent/types';
import PreferredName from './components/PreferredName';
import MostImportantToMe from './components/MostImportantToMe';
import WhoImportantToMe from './components/WhoImportantToMe';
import HowToCommunicate from './components/HowToCommunicate';
import MyWellness from './components/MyWellness';
import SupportMe from './components/SupportMe';
import KnowAboutMe from './components/KnowAboutMe';
import SupportedBy from './components/SupportedBy';
import MultimediaFile from './components/MultimediaFile';
import { AboutMeTypes, AboutMeTypesFromData, Link } from './StepThreeTypes';
import validateReviewDateMessage from './utils';
import MentalCapacityAssessment from './components/mental-capacity-assessment';
import { MentalCapacityAssessmentType } from './components/mental-capacity-assessment/utils';
import LearnMoreAboutMe from './components/LearnMoreAboutMe';
import MultiMediaUrl from './components/MultiMediaUrl';
import DateLastUpdated from './components/DateLastUpdated';
import PleaseDoAndDoNot from './components/PleaseDoAndDoNot';
import VoiceTranscribing from './components/VoiceTranscribing';
import PromptScript from './components/VoiceTranscribing/PromptScript';

interface StepThreeProps {
  slug: string | undefined;
  role: string;
  userId: string;
  residentFullName: string;
  changeStep: (status: StepChangeDirection) => void;
  threeStepsData: ThreeStepsDetails;
  handleGetResidentDetails: () => Promise<void>;
  onSubmitThreeStepsData: (
    submitValues: ThreeStepsDetails,
    position: SuccessNotiPositionEnum
  ) => void;
  callApiStatus: SubmitStatus;
  showSuccessNotification: ShowSuccessNotificationType;
  refreshLogs: boolean;
}

const StepThreeComponent = ({
  slug,
  role,
  userId,
  residentFullName,
  changeStep,
  threeStepsData,
  handleGetResidentDetails,
  onSubmitThreeStepsData,
  callApiStatus,
  showSuccessNotification,
  refreshLogs,
}: StepThreeProps) => {
  const [reviewDate, setReviewDate] = useState<ReviewDateType>({
    dateDetails: { day: '', month: '', year: '' },
    isNotRelevant: false,
  });
  const { errors, setErrors } = useErrors();
  const [reviewDateFromAPI, setReviewDateFromAPI] = useState<string>('');
  const [acpConsentDetails, setAcpConsentDetails] = useState<any>({
    yesValue: '',
    noValue: '',
    bestInterest: {
      status: '',
      data: {},
    },
  });

  const [preferredName, setPreferredName] = useState('');
  const [mentalCapacityAssessment, setMentalCapacityAssessment] =
    useState<MentalCapacityAssessmentType>({
      hasAssessment: '',
      dateCompleted: { day: '', month: '', year: '' },
      capacityAssessmentEntry: '',
      locationOfMca: '',
      mcaFormList: [],
    });
  const [uploadedMcaFormListFromData, setUploadedMcaFormListFromData] =
    useState<FileListData[]>([]);

  const [mentalCapacity, setMentalCapacity] = useState({
    mentalCapacityStatus: '',
  });
  const [acpConsentValues, setAcpConsentValues] = useState<ACPConsentData>({
    yesValue: '',
    noValue: '',
    bestInterest: {
      status: '',
      data: {},
    },
  });

  const [aboutMe, setAboutMe] = useState<AboutMeTypes>({
    mostImportantToMe: '',
    whoImportantToMe: '',
    howToCommnunicate: '',
    myWellness: '',
    plsdoAndonot: '',
    remind: '',
    supportMe: '',
    knowAboutMe: '',
    recordUpdateDate: { day: '', month: '', year: '' },
    supportedBy: '',
    multimediaFiles: [],
    multiMediaUrl: [],
    voiceTranscribingConsent: {
      understandsStatement: '',
      happyForTranscribingService: '',
      otherReason: '',
    },
  });
  const [aboutMeFromData, setAboutMeFromData] = useState<AboutMeTypesFromData>({
    mostImportantToMe: '',
    whoImportantToMe: '',
    howToCommnunicate: '',
    myWellness: '',
    plsdoAndonot: '',
    remind: '',
    supportMe: '',
    knowAboutMe: '',
    recordUpdateDate: '',
    supportedBy: '',
    multimediaFiles: [],
    multiMediaUrl: [],
    voiceTranscribingConsent: {
      understandsStatement: '',
      happyForTranscribingService: '',
      otherReason: '',
    },
  });
  const [mediaUrlList, setMediaUrlList] = useState<Link[]>([]);
  const [mediaUrl, setMediaUrl] = useState('');

  const onFinish = (e: any, position: SuccessNotiPositionEnum) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];
    const reviewDateErrorMessage = validateReviewDateMessage(
      reviewDateFromAPI,
      reviewDate.dateDetails
    );

    validationErrors = updateErrors(
      validationErrors,
      'reviewDate',
      reviewDateErrorMessage
    );

    let formattedReviewDate = '';
    if (!reviewDateErrorMessage) {
      if (reviewDate.isNotRelevant) {
        formattedReviewDate = 'not relevant';
      } else {
        formattedReviewDate = formatDate(reviewDate.dateDetails, 'YYYY-MM-DD');
      }
    }

    const dateCompletedErrorMessage = validatePastDateMessage(
      mentalCapacityAssessment.dateCompleted
    );
    validationErrors = updateErrors(
      validationErrors,
      'dateCompleted',
      dateCompletedErrorMessage
    );

    const recordUpdateErrorMessage = validatePastDateMessage(
      aboutMe.recordUpdateDate
    );
    validationErrors = updateErrors(
      validationErrors,
      'recordUpdateDate',
      recordUpdateErrorMessage
    );

    setErrors(prev => validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    const updatedMediaUrlList = [...(mediaUrlList || [])];

    if (mediaUrl.trim()) {
      updatedMediaUrlList.push({
        id: uuidv4(),
        url: mediaUrl,
      });
      setMediaUrlList(updatedMediaUrlList);
    }

    const submitValues: any = {
      mentalCapacityAssessment: {
        ...mentalCapacityAssessment,
        dateCompleted: formatDate(
          mentalCapacityAssessment.dateCompleted,
          'YYYY-MM-DD'
        ),
      },
      mentalCapacityStatus: mentalCapacity.mentalCapacityStatus,
      reviewDate: formattedReviewDate,
      aboutMe: {
        ...aboutMe,
        recordUpdateDate: formatDate(aboutMe.recordUpdateDate, 'YYYY-MM-DD'),
        multiMediaUrl: updatedMediaUrlList,
      },
      acpConsent: acpConsentValues,
      preferredName,
    };
    const transformedData = questions.map(q => ({
      question: q.question,
      key: q.key,
      answer: submitValues[q.key] || '',
    }));
    onSubmitThreeStepsData({ step3: transformedData }, position);
    setMediaUrl('');
  };

  const extractAnswer = (answer: MultipleStepsAnswer): string => {
    if (typeof answer === 'string') {
      return answer;
    }
    if (Array.isArray(answer) && answer.length > 0) {
      return answer[0].name; // Or another property as needed
    }
    return '';
  };

  useEffect(() => {
    handleGetResidentDetails();
  }, [handleGetResidentDetails]);

  useEffect(() => {
    if (threeStepsData && threeStepsData.step3) {
      const { step3 } = threeStepsData;

      const mentalCapacityAssessmentFromData: any = threeStepsData?.step3.find(
        field => field.key === 'mentalCapacityAssessment'
      )?.answer;
      if (mentalCapacityAssessmentFromData) {
        const parsedDateCompleted = parseFormattedDate(
          mentalCapacityAssessmentFromData.dateCompleted
        );
        setMentalCapacityAssessment({
          ...mentalCapacityAssessmentFromData,
          dateCompleted: parsedDateCompleted,
        });

        setUploadedMcaFormListFromData(
          mentalCapacityAssessmentFromData.mcaFormList
        );
      }

      setMentalCapacity({
        mentalCapacityStatus: extractAnswer(
          step3.find(ob => ob.key === 'mentalCapacityStatus')?.answer || ''
        ),
      });

      const acpConsentData: any = threeStepsData?.step3.find(
        field => field.key === 'acpConsent'
      )?.answer;
      if (acpConsentData) {
        setAcpConsentValues(acpConsentData);
        setAcpConsentDetails(acpConsentData);
      }

      const reviewDateData = threeStepsData?.step3.find(
        field => field.key === 'reviewDate'
      )?.answer as string;

      setReviewDateFromAPI(reviewDateData);

      const preferredNameData = threeStepsData?.step3.find(
        field => field.key === 'preferredName'
      )?.answer as string;
      setPreferredName(preferredNameData);

      const aboutMeData = threeStepsData?.step3.find(
        field => field.key === 'aboutMe'
      )?.answer;

      if (aboutMeData) {
        setAboutMe(aboutMeData as any);
        setAboutMeFromData(aboutMeData as any);
      }
    }
  }, [threeStepsData]);

  const onSaveInMca = (e: any, position: SuccessNotiPositionEnum) => {
    onFinish(e, position);
  };

  const handleSave = (position: SuccessNotiPositionEnum) => {
    return (
      <>
        <button
          type="submit"
          className="govuk-button"
          data-module="govuk-button"
          disabled={showSuccessNotification.btnLoading}
          onClick={e => onFinish(e, position)}
        >
          Save and continue
        </button>
        {showSuccessNotification.status &&
          showSuccessNotification.position === position && (
            <div className="mt-10">
              <SuccessNotificationBanner
                title={successMessageInMiddleOfSteps.title}
                content={successMessageInMiddleOfSteps.content}
                duration={3000}
                noScrollToTop
              />
            </div>
          )}
      </>
    );
  };

  return (
    <div className="mt-5 md:mt-10">
      <h1 className="govuk-heading-xl">{residentFullName} - About me</h1>
      <div className="govuk-body">
        This is a record of the things that a person feels it is important to
        communicate about their needs, strengths, values, concerns and
        preferences to others providing support and care.
      </div>
      <LearnMoreAboutMe />
      {errors.length > 0 && <NewErrorSummary errors={errors} />}
      <form
      // disabled={role === Role.TeamMemberViewOnly}
      >
        <h1 className="govuk-heading-l mt-10 govuk-bp:mt-14">
          {residentFullName} - Mental capacity and consent
        </h1>
        <div className="govuk-body">
          A mental capacity and consent assessment should be completed before
          documenting their wishes. Each time this Advance Care Plan is reviewed
          and updated the person&apos;s mental capacity and consent should be
          reviewed.
        </div>
        <MentalCapacityAssessment
          userId={userId}
          mentalCapacityAssessment={mentalCapacityAssessment}
          setMentalCapacityAssessment={setMentalCapacityAssessment}
          uploadedMcaFormListFromData={uploadedMcaFormListFromData}
          showSuccessNotification={showSuccessNotification}
          onSaveInMca={onSaveInMca}
        />
        <MentalCapacitySafeGuarding
          mentalCapacity={mentalCapacity}
          setMentalCapacity={setMentalCapacity}
        />
        <ACPConsent
          acpConsentDetails={acpConsentDetails}
          setAcpConsentValues={setAcpConsentValues}
        />
        <ReviewDate
          reviewDateFromAPI={reviewDateFromAPI}
          reviewDate={reviewDate}
          setReviewDate={setReviewDate}
        />
        <PreferredName
          residentFullName={residentFullName}
          preferredName={preferredName}
          setPreferredName={setPreferredName}
        />
        <VoiceTranscribing aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.SECOND)}
        <PromptScript />
        <MostImportantToMe aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.FIRST)}
        <WhoImportantToMe aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.SECOND)}
        <HowToCommunicate aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.THIRD)}
        <MyWellness aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.FOURTH)}
        <PleaseDoAndDoNot aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.FIFTH)}
        <SupportMe aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.SIXTH)}
        <KnowAboutMe aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.SEVENTH)}
        <SupportedBy aboutMe={aboutMe} setAboutMe={setAboutMe} />
        {handleSave(SuccessNotiPositionEnum.EIGHTH)}
        <MultimediaFile
          aboutMe={aboutMe}
          setAboutMe={setAboutMe}
          userId={userId}
          aboutMeFromData={aboutMeFromData}
        />
        <MultiMediaUrl
          aboutMeFromData={aboutMeFromData}
          mediaUrlList={mediaUrlList}
          setMediaUrlList={setMediaUrlList}
          mediaUrl={mediaUrl}
          setMediaUrl={setMediaUrl}
        />
        <DateLastUpdated refreshLogs={refreshLogs} userId={userId} />
        <div className="flex items-center justify-between w-full mt-10 govuk-bp:mt-14 gap-5">
          <div className="govuk-button-group w-full justify-between">
            <button
              type="submit"
              disabled={callApiStatus.loading}
              className="govuk-button govuk-button--secondary"
              onClick={() => changeStep(StepChangeDirection.Previous)}
            >
              Previous
            </button>
            <div className="govuk-button-group w-full govuk-bp:w-fit">
              <button
                type="submit"
                className="govuk-button"
                data-module="govuk-button"
                disabled={callApiStatus.loading}
                onClick={e => {
                  onFinish(e, SuccessNotiPositionEnum.LAST);
                }}
              >
                Save
              </button>
              <button
                type="submit"
                className="govuk-button"
                data-module="govuk-button"
                disabled={callApiStatus.loading}
                onClick={() => changeStep(StepChangeDirection.Next)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        {callApiStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            title="This document has been saved."
            content={callApiStatus.message}
            duration={3000}
            noScrollToTop
          />
        )}
      </form>
    </div>
  );
};

const StepThree: React.FC<StepThreeProps> = props => {
  return (
    <ErrorsProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <StepThreeComponent {...props} />
    </ErrorsProvider>
  );
};

export default StepThree;
